
import Vue, { PropType } from 'vue';
import { Device, Floor } from '@/interfaces/locations';
import { Controller } from '@/interfaces/controllers';
import {
	BoundsLiteral,
	CRS,
	latLng,
	LatLng,
	latLngBounds,
	LeafletEvent,
	MapOptions,
} from 'leaflet';
import DeviceMarker from '@/components/common/DeviceMarker.vue';
import DevicemapIconsLegend from '@/components/configuration/DevicemapIconsLegend.vue';
import { LMap } from 'vue2-leaflet';

export interface Position {
	lat: number | null;
	lng: number | null;
}
export type StatefulDevice = Device & { selected: boolean } & { serial_number: boolean };

export interface DeviceMarkerMap {
	position: Position;
	id: number;
	name: string;
	guid?: string;
	channel?: number;
	active?: boolean;
	draggable?: boolean;
	visible: boolean;
	isLuminaire: boolean;
	isMovementSensor: boolean;
	isLightLevelSensor: boolean;
	isGateway: boolean;
	isOtherSensor: boolean;
}

interface ComponentData {
	mapComponent: LMap | null;
	zoom: number;
	center: LatLng;
	bounds: BoundsLiteral;
	editRectangleBounds: BoundsLiteral;
	crs: CRS;
	options: MapOptions;
	deviceFilter: string;
	devices: StatefulDevice[];
	mapHeight: number;
}

export default Vue.extend({
	components: { DeviceMarker, DevicemapIconsLegend },

	props: {
		editMode: { type: Boolean, default: false },
		floors: { type: Array as PropType<Floor[]> },
		controllers: { type: Array as PropType<Controller[]> },
		localDevices: { type: Array as PropType<StatefulDevice[]> },
		unChangeableLuminaires: { type: Array as PropType<Device[]> },
		unChangeableSwitches: { type: Array as PropType<Device[]> },
		unChangeableGateways: { type: Array as PropType<Device[]> },
		selectedFloor: { type: Object as PropType<Floor> },
		userPermission: { type: Boolean, default: true },
	},

	data(): ComponentData {
		return {
			mapComponent: null,
			zoom: 3,
			center: latLng(0, 0),
			bounds: [
				[100, -100],
				[-100, 100],
			],
			editRectangleBounds: [
				[10, -10],
				[-10, 10],
			],
			crs: CRS.Simple,
			options: {
				attributionControl: false,
				zoomControl: false,
				scrollWheelZoom: true,
			},
			deviceFilter: '',
			devices: [],
			mapHeight: 525,
		};
	},

	watch: {
		localDevices: {
			deep: true,
			immediate: true,
			handler(devices) {
				this.devices = devices;
			},
		},
	},

	computed: {
		deviceMarkers(): DeviceMarkerMap[] {
			const devicesWithCoordinates = this.localDevices.filter(
				(device) => device.local_longitude !== null && device.local_latitude !== null
			);

			return devicesWithCoordinates.map((device) => {
				const controller = this.controllers?.find(
					(controller) => controller.id === device.fk_controller
				);
				return {
					id: device.id,
					name: device.name,
					guid: controller?.guid.slice(-4),
					channel: controller?.radio_channel,
					position: { lat: device.local_latitude, lng: device.local_longitude },
					active: device.selected,
					draggable: this.editMode,
					visible: true,
					isLuminaire: Boolean(device.is_luminaire),
					isMovementSensor: Boolean(device.is_movement_sensor),
					isLightLevelSensor: Boolean(device.is_lightlevel_sensor),
					isGateway: !!device.serial_number,
					isOtherSensor: Boolean(device.is_other_sensor),
				};
			});
		},

		devicesWoCoordinates(): (StatefulDevice & { guid?: string })[] {
			return this.localDevices
				.map((device: StatefulDevice & { guid?: string }) => {
					device.guid = this.controllers?.find(
						(controller) => controller.id === device.fk_controller
					)?.guid;
					return device;
				})
				.filter((device: StatefulDevice & { guid?: string }) => {
					const deviceName = device.guid ? `${device.name} - ${device.guid}` : device.name;
					return (
						(device.local_longitude === null || device.local_latitude === null) &&
						this.containsExactSequence(deviceName, this.deviceFilter)
					);
				});
		},
	},

	methods: {
		deviceMapReady(): void {
			this.mapComponent = this.$refs.deviceMap as LMap;
		},

		changeFloor(floor: Floor) {
			this.$emit('changeFloor', floor);
		},

		dragDevice(device: Device) {
			if (this.editMode) {
				const rectangleCenterLatLng = latLngBounds(this.editRectangleBounds).getCenter();
				device.local_latitude = rectangleCenterLatLng.lat;
				device.local_longitude = rectangleCenterLatLng.lng;
			}
		},

		locUpdated(position: Position, deviceId: number) {
			const updatedDevice = this.devices.find((device: Device) => device.id === deviceId);
			if (updatedDevice !== undefined) {
				updatedDevice.local_latitude = position.lat;
				updatedDevice.local_longitude = position.lng;
			}
		},

		saveCoordinates() {
			const luminairesToUpdate: Device[] = [];
			const switchesToUpdate: Device[] = [];
			const gatewaysToUpdate: Device[] = [];

			for (const device of this.devices) {
				const originalLuminaire = this.unChangeableLuminaires.find(
					(originalLuminaire: Device) => originalLuminaire.id === device.id
				);

				if (originalLuminaire) {
					if (
						device.local_latitude !== originalLuminaire.local_latitude ||
						device.local_longitude !== originalLuminaire.local_longitude
					) {
						luminairesToUpdate.push(device);
					}
					continue;
				}

				const originalGateway = this.unChangeableGateways.find(
					(originalGateway: Device) => originalGateway.id === device.id
				);

				if (originalGateway) {
					if (
						device.local_latitude !== originalGateway.local_latitude ||
						device.local_longitude !== originalGateway.local_longitude
					) {
						gatewaysToUpdate.push(device);
					}
					continue;
				}

				const originalSwitch = this.unChangeableSwitches.find(
					(originalSwitch: Device) => originalSwitch.id === device.id
				);

				if (originalSwitch) {
					if (
						device.local_latitude !== originalSwitch.local_latitude ||
						device.local_longitude !== originalSwitch.local_longitude
					) {
						switchesToUpdate.push(device);
					}
				}
			}
			this.$emit('saveCoordinates', luminairesToUpdate, switchesToUpdate, gatewaysToUpdate);
			this.$emit('changeEditMode', false);
			this.editRectangleBounds = [
				[10, -10],
				[-10, 10],
			];
		},

		onMarkerClick(id: number) {
			if (!this.editMode) {
				const selectedDevice = this.deviceMarkers.find((device) => device.id === id);
				this.$emit('device-selected', id, selectedDevice!.isGateway ? 'gateways' : 'devices');
			}
		},

		confirmEditMode() {
			this.$emit('changeEditMode', true);
		},

		async cancelEditMode() {
			for (const device of this.devices) {
				const originalLuminaire = this.unChangeableLuminaires.find(
					(originalDevice) => originalDevice.id === device.id
				);

				if (originalLuminaire) {
					device.local_latitude = originalLuminaire.local_latitude;
					device.local_longitude = originalLuminaire.local_longitude;
					await this.$nextTick();
				}

				const originalSwitch = this.unChangeableSwitches.find(
					(originalDevice) => originalDevice.id === device.id
				);

				if (originalSwitch) {
					device.local_latitude = originalSwitch.local_latitude;
					device.local_longitude = originalSwitch.local_longitude;
					await this.$nextTick();
				}

				const originalGateway = this.unChangeableGateways.find(
					(originalDevice) => originalDevice.id === device.id
				);

				if (originalGateway) {
					device.local_latitude = originalGateway.local_latitude;
					device.local_longitude = originalGateway.local_longitude;
					await this.$nextTick();
				}
			}
			this.$emit('changeEditMode', false);
			this.deviceFilter = '';
			this.editRectangleBounds = [
				[10, -10],
				[-10, 10],
			];
		},

		dragendRectangle(event: LeafletEvent) {
			this.editRectangleBounds = event.target.getLatLngs();
		},

		resetDeviceLocation(deviceId: number): void {
			const selectedDevice = this.devices.find((device) => device.id === deviceId);
			if (
				selectedDevice &&
				selectedDevice.local_latitude !== null &&
				selectedDevice.local_longitude !== null
			) {
				const rectangleCenterLatLng = latLngBounds(this.editRectangleBounds).getCenter();
				selectedDevice.local_latitude = rectangleCenterLatLng.lat;
				selectedDevice.local_longitude = rectangleCenterLatLng.lng;
			}
		},

		containsExactSequence(string: string, sequence: string): boolean {
			if (!sequence) {
				return true;
			}
			const lowerCaseString = string.toLowerCase();
			const lowerCaseSequence = sequence.toLowerCase();
			return lowerCaseString.includes(lowerCaseSequence);
		},

		disableMapScroll(): void {
			if (this.mapComponent) {
				this.mapComponent.mapObject.scrollWheelZoom.disable();
			}
		},

		enableMapScroll() {
			if (this.mapComponent) {
				this.mapComponent.mapObject.scrollWheelZoom.enable();
			}
		},
	},
});
